import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';

import Home from './Home';
import MobileWrapperRegistration from 'pages/Register/MobileWrapperRegistration/MobileWrapperRegistration';
import mobileAuthBackground from 'pages/Register/MobileWrapperRegistration/mobile-auth-background.webp';
import API from 'api';
import { setAuthFromToken } from 'actions/authActions';
import { redirect } from 'utils';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import {
  MOBILE_FAMILY_ACCOUNT_TOKEN_KEY,
  MOBILE_FAMILY_STATE_RESET_REQUIRED,
  MOBILE_FAMILY_STATE_SUSPENDED,
  clearMobileFamilyForRegistration,
  getMobileFamilyState,
} from 'utils/mobileFamilyAccess';


const getPrimaryMobileMemberUsername = (members = []) => {
  const originalMember = members.find((member) => member?.originalMember && member?.username);
  if (originalMember?.username) return originalMember.username;

  const firstMember = members.find((member) => member?.username);
  return firstMember?.username || '';
};

const MobileFamilySuspended = () => (
  <div
    style={{
      width: '100vw',
      minHeight: '100vh',
      height: '100svh',
      display: 'grid',
      placeItems: 'center',
      padding: 28,
      boxSizing: 'border-box',
      textAlign: 'center',
      backgroundColor: '#fff',
      backgroundImage: `url(${mobileAuthBackground})`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      color: '#151b26',
    }}
  >
    <div>
      <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 10 }}>Family access is suspended</div>
      <div style={{ fontSize: 17, lineHeight: 1.45 }}>Please contact Family Learning Company support to restore access.</div>
    </div>
  </div>
);

const MobileWrapperAutoLogin = ({ history }) => {
  const dispatch = useDispatch();
  const familyAccountToken = useMemo(() => localStorage.getItem(MOBILE_FAMILY_ACCOUNT_TOKEN_KEY) || '', []);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const autoLoginPrimaryMember = async () => {
      if (!familyAccountToken) {
        history.replace('/');
        return;
      }

      try {
        const session = await API.mobileWrapper.getFamilySession(familyAccountToken);
        if (!session?.success) {
          if (Number(session?.statusCode) === 404 || /mobile family account was not found/i.test(String(session?.error || ''))) {
            clearMobileFamilyForRegistration();
            window.location.replace('/');
            return;
          }
          throw new Error(session?.error || 'Unable to load this family account.');
        }

        const familyState = getMobileFamilyState(session);
        if (familyState === MOBILE_FAMILY_STATE_RESET_REQUIRED) {
          clearMobileFamilyForRegistration();
          window.location.replace('/');
          return;
        }
        if (familyState === MOBILE_FAMILY_STATE_SUSPENDED) {
          history.replace('/?mobileFamily=suspended');
          return;
        }

        const username = getPrimaryMobileMemberUsername(session.members);
        if (!username) throw new Error('No mobile learner was found for this family account.');

        const result = await API.mobileWrapper.loginFamilyMembers({
          familyAccountToken,
          usernames: [username],
        });
        if (!result?.success || !result?.token) throw new Error(result?.error || 'Unable to start the learning session.');
        if (!active) return;

        dispatch(setAuthFromToken(result.token));
        history.replace('/play');
      } catch (requestError) {
        if (!active) return;
        setError(requestError?.response?.data?.error || requestError?.message || 'Unable to start the learning session.');
      }
    };

    autoLoginPrimaryMember();
    return () => { active = false; };
  }, [dispatch, familyAccountToken, history]);

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', fontSize: 22, fontWeight: 700 }}>
      {error || 'Starting your learning session...'}
    </div>
  );
};

MobileWrapperAutoLogin.propTypes = {
  history: PropTypes.object.isRequired,
};

const MobileAppEntry = (props) => {
  if (isMobileAppRuntime()) {
    const suspended = new URLSearchParams(props.location?.search || '').get('mobileFamily') === 'suspended';
    if (suspended) {
      return <MobileFamilySuspended />;
    }
    if (props.auth?.isAuthenticated) {
      return <Redirect to={redirect(props.auth)} />;
    }

    if (localStorage.getItem('jwtToken')) {
      return null;
    }

    if (localStorage.getItem(MOBILE_FAMILY_ACCOUNT_TOKEN_KEY)) {
      return <MobileWrapperAutoLogin history={props.history} />;
    }

    return <MobileWrapperRegistration history={props.history} />;
  }

  return <Home {...props} />;
};

MobileAppEntry.propTypes = {
  auth: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object,
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(MobileAppEntry);
